/** @forge-header
 * @id          ExchangeOrderBook
 * @role        Vertical order book ladder | asks-on-top, bids-below, depth bars, sweep cost
 * @pattern     component
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @upstream    nbaConsolidated/types:BookLevel,Venue | atoms/Money:Money | infra/utils:cn
 * @downstream  GameDetailPanel | StreamRift/BookDepthPanel
 * @exports     ExchangeOrderBook:component | SweepInfo:interface
 * @test        ExchangeOrderBook.test.tsx | .compiler.test.tsx | .consumers.test.tsx
 */

// @ai-invariant priceCents on BookLevel is always 0..100 cents (≤2dp on sub-cent-tick markets) — depth dollars = price * size / 100
import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/infra/utils';
import { Money } from '@/components/atoms/Money';
import { roundToCentiCents } from '@/lib/pricing/subCent';
import type { BookLevel } from '@/types';
import { VENUE_QUANTITY_LABEL, VENUE_SHORT, VENUE_TONE, type TradingVenue } from '@/lib/venues';

/** Ladder price label — grain-canonical, trimmed ("58.25¢", "0.1¢", "55¢").
 *  The old Math.round displayed deci_cent levels as integers even though the
 *  underlying level (and its volume) was exact — prod-caught 2026-07-17. */
const formatLevelCents = (cents: number): string => `${roundToCentiCents(cents)}¢`;

/** Computed level with cumulative depth for display */
interface ComputedLevel {
  priceCents: number;
  size: number;
  /** Dollar cost at this single level: price * size / 100 */
  levelDollars: number;
  /** Running cumulative $ from best price outward */
  cumulativeDollars: number;
  /** Running cumulative contracts from best price outward */
  cumulativeContracts: number;
}

export interface SweepInfo {
  /** Price level clicked */
  priceCents: number;
  /** Total contracts in the sweep */
  totalContracts: number;
  /** Contracts at the clicked level, excluding all other levels. */
  levelContracts?: number;
  /** Total dollar cost of the sweep */
  totalDollars: number;
  /** Count of price levels from best through clicked */
  levelCount: number;
  side: 'bid' | 'ask';
}

interface ExchangeOrderBookProps {
  /** YES bids (descending by price) */
  bids: BookLevel[];
  /** YES asks derived from NO bids (ascending by price expected, will be sorted) */
  asks: BookLevel[];
  /** Last traded price in cents */
  lastPrice?: number | null;
  /** Spread in cents */
  spread?: number | null;
  /** Venue for column label ('Contracts' vs 'Shares') */
  venue: TradingVenue;
  /** Header label (team name or market name) */
  teamLabel: string;
  /** Max price levels per side (default 8) */
  maxLevels?: number;
  /** Click handler with sweep info */
  onLevelClick?: (info: SweepInfo) => void;
  /** Optional node rendered at the right of the header (e.g. a freshness
   *  indicator). Kept as a slot so this ladder stays decoupled from the store. */
  headerRight?: ReactNode;
  /** Highlighted sweep level (hovered) */
  className?: string;
}

function computeLevels(levels: BookLevel[], side: 'bid' | 'ask'): ComputedLevel[] {
  // Aggregate duplicate price levels by summing size. Upstream books should
  // already be one-row-per-price, but the polymarket path mirrors twice
  // (marketStore.buildPolyBooks → GameDetailPanel) and can collapse distinct
  // levels onto the same priceCents; without aggregation we'd render two rows
  // at the same price (and React would warn about duplicate keys).
  const byPrice = new Map<number, number>();
  for (const lvl of levels) {
    byPrice.set(lvl.priceCents, (byPrice.get(lvl.priceCents) ?? 0) + lvl.size);
  }
  const merged: BookLevel[] = Array.from(byPrice, ([priceCents, size]) => ({ priceCents, size }));

  // Bids: descending (best = highest first). Accumulate from top.
  // Asks: ascending (best = lowest first). Accumulate from bottom displayed (which is best = lowest).
  // For asks display, we show worst (highest) at top, best (lowest) at bottom.
  // So we sort ascending, then reverse for display, but accumulate in ascending order.

  if (side === 'bid') {
    // Bids: sorted descending. Best bid at top. Accumulate from top.
    const sorted = [...merged].sort((a, b) => b.priceCents - a.priceCents);
    let cumDollars = 0;
    let cumContracts = 0;
    return sorted.map((lvl) => {
      const levelDollars = (lvl.priceCents * lvl.size) / 100;
      cumDollars += levelDollars;
      cumContracts += lvl.size;
      return {
        priceCents: lvl.priceCents,
        size: lvl.size,
        levelDollars,
        cumulativeDollars: cumDollars,
        cumulativeContracts: cumContracts,
      };
    });
  } else {
    // Asks: sort ascending first (best ask = lowest price first)
    const sorted = [...merged].sort((a, b) => a.priceCents - b.priceCents);
    let cumDollars = 0;
    let cumContracts = 0;
    const withCumulative = sorted.map((lvl) => {
      const levelDollars = (lvl.priceCents * lvl.size) / 100;
      cumDollars += levelDollars;
      cumContracts += lvl.size;
      return {
        priceCents: lvl.priceCents,
        size: lvl.size,
        levelDollars,
        cumulativeDollars: cumDollars,
        cumulativeContracts: cumContracts,
      };
    });
    // Reverse for display: worst ask (highest price) at top
    return withCumulative.reverse();
  }
}

function formatCompactDollars(dollars: number): string {
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 10_000) return `$${(dollars / 1_000).toFixed(0)}K`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return `$${dollars.toFixed(2)}`;
}

function formatContracts(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 100_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

export function ExchangeOrderBook({
  bids,
  asks,
  lastPrice,
  spread,
  venue,
  teamLabel,
  maxLevels = 8,
  onLevelClick,
  headerRight,
  className,
}: ExchangeOrderBookProps) {
  'use memo'; // #198 React Compiler wave A3 — docs/react-compiler-clusters/wave-a3-exchangeorderbook.md
  const [hoveredLevel, setHoveredLevel] = useState<{
    price: number;
    side: 'bid' | 'ask';
  } | null>(null);

  const contractsLabel = VENUE_QUANTITY_LABEL[venue];
  const venueBadge = VENUE_SHORT[venue];
  const venueBadgeClass = VENUE_TONE[venue];

  const computedAsks = useMemo(() => computeLevels(asks, 'ask'), [asks]);
  const computedBids = useMemo(() => computeLevels(bids, 'bid'), [bids]);

  const displayAsks = computedAsks.slice(-maxLevels);
  const displayBids = computedBids.slice(0, maxLevels);

  // Max cumulative for depth bar scaling (use the larger of ask/bid max)
  const maxCumDollars = Math.max(
    displayAsks.length > 0 ? displayAsks[0].cumulativeDollars : 0, // worst ask has largest cumulative (first in display)
    displayBids.length > 0 ? (displayBids[displayBids.length - 1]?.cumulativeDollars ?? 0) : 0
  );

  const handleLevelClick = (level: ComputedLevel, side: 'bid' | 'ask', levelCount: number) => {
    onLevelClick?.({
      priceCents: level.priceCents,
      totalContracts: level.cumulativeContracts,
      levelContracts: level.size,
      totalDollars: level.cumulativeDollars,
      levelCount,
      side,
    });
  };

  // #1113 (T7 item 6): with no click handler the ladder is reference only — no
  // pointer, no hover tint, no sweep highlight. With one, every class below is
  // exactly what it was. The hover state stays declared either way ('use memo').
  const interactive = onLevelClick != null;
  // Reference-only rows have no mouseleave, so a hover held when the handler goes
  // would come back as a phantom sweep with the handler. Forget it now.
  if (!interactive && hoveredLevel !== null) setHoveredLevel(null);

  // Determine if a level is within the hover sweep range
  const isInSweep = (level: ComputedLevel, side: 'bid' | 'ask'): boolean => {
    if (!interactive || !hoveredLevel || hoveredLevel.side !== side) return false;
    if (side === 'ask') {
      // Sweep asks from best (lowest) to hovered. In display order (reversed),
      // hovered is somewhere above; all levels below it (including it) are in sweep.
      return level.priceCents <= hoveredLevel.price;
    } else {
      // Sweep bids from best (highest) to hovered. All levels above it (including it) are in sweep.
      return level.priceCents >= hoveredLevel.price;
    }
  };

  return (
    <div className={cn('bg-card border-border overflow-hidden rounded-lg border', className)}
      title={interactive ? undefined : "Reference only; this book can't seed the ticket."}>
      {/* Header */}
      {/* A long team label truncates inside its own box; the venue badge and
          the right-hand status never shrink or get pushed out of the card. */}
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', venueBadgeClass)}
          >
            {venueBadge}
          </span>
          <span className="min-w-0 truncate text-sm font-medium" title={teamLabel}>{teamLabel}</span>
        </div>
        {headerRight != null && <div className="flex shrink-0 items-center">{headerRight}</div>}
      </div>

      {/* Column headers */}
      <div className="text-muted-foreground border-border grid grid-cols-3 border-b px-3 py-1 text-[10px] uppercase tracking-wider">
        <span>Price</span>
        <span className="text-right">{contractsLabel}</span>
        <span className="text-right">Total</span>
      </div>

      {/* Asks (worst at top, best at bottom → toward spread) */}
      <div className="relative">
        {displayAsks.length === 0 ? (
          <div className="text-muted-foreground py-3 text-center text-xs">No asks</div>
        ) : (
          displayAsks.map((level, index) => {
            const depthPct =
              maxCumDollars > 0 ? (level.cumulativeDollars / maxCumDollars) * 100 : 0;
            const swept = isInSweep(level, 'ask');
            return (
              <div
                key={`ask-${level.priceCents}`}
                data-testid="order-book-level"
                data-side="ask"
                className={interactive ? cn(
                  'group relative grid cursor-pointer grid-cols-3 px-3 py-1 font-mono text-xs transition-all',
                  swept ? 'bg-red-500/20' : 'hover:bg-red-500/10'
                ) : 'group relative grid grid-cols-3 px-3 py-1 font-mono text-xs transition-all'}
                onClick={interactive ? () => handleLevelClick(level, 'ask', displayAsks.length - index) : undefined}
                onMouseEnter={interactive ? () => setHoveredLevel({ price: level.priceCents, side: 'ask' }) : undefined}
                onMouseLeave={interactive ? () => setHoveredLevel(null) : undefined}
              >
                {/* Depth bar — left-anchored to match the bid side */}
                <div
                  className={cn(
                    'pointer-events-none absolute inset-y-0 left-0 transition-colors',
                    swept ? 'bg-red-500/25' : 'bg-red-500/10'
                  )}
                  style={{ width: `${Math.min(depthPct, 100)}%` }}
                />
                <span className="relative z-10 font-semibold text-red-400">
                  {formatLevelCents(level.priceCents)}
                </span>
                <span className="text-muted-foreground relative z-10 text-right">
                  {/* Key by the formatted string so animate-flash-neutral
                      only re-fires when the displayed value actually
                      changes. Keying by raw `level.size` made Polymarket
                      books pulse every tick: Poly sends fractional sizes
                      and tiny decimal jitter (1000 → 1000.0001) gave the
                      visible "1,000" a different React key each frame,
                      remounting the span and re-running the CSS animation
                      even though nothing visible had changed. */}
                  <span
                    key={formatContracts(level.size)}
                    className="animate-flash-neutral inline-block"
                  >
                    {formatContracts(level.size)}
                  </span>
                </span>
                <span className="text-muted-foreground relative z-10 text-right">
                  <span
                    key={formatCompactDollars(level.cumulativeDollars)}
                    className="animate-flash-neutral inline-block"
                  >
                    {formatCompactDollars(level.cumulativeDollars)}
                  </span>
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Spread divider */}
      <div className="border-border bg-muted/40 flex items-center justify-between border-y px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase text-red-400">Asks</span>
          {lastPrice != null && (
            <span className="font-mono text-xs font-medium">Last {formatLevelCents(lastPrice)}</span>
          )}
          <span className="text-[10px] font-semibold uppercase text-green-400">Bids</span>
        </div>
        {spread != null && (
          // Grain-round defends against float dust from ask−bid subtraction
          // upstream (58.3 − 58.2 = 0.0999999…) — renders "0.1¢" exactly.
          <span className="text-muted-foreground text-[10px]">Spread: {formatLevelCents(spread)}</span>
        )}
      </div>

      {/* Bids (best at top, worst at bottom → away from spread) */}
      <div className="relative">
        {displayBids.length === 0 ? (
          <div className="text-muted-foreground py-3 text-center text-xs">No bids</div>
        ) : (
          displayBids.map((level, index) => {
            const depthPct =
              maxCumDollars > 0 ? (level.cumulativeDollars / maxCumDollars) * 100 : 0;
            const swept = isInSweep(level, 'bid');
            return (
              <div
                key={`bid-${level.priceCents}`}
                data-testid="order-book-level"
                data-side="bid"
                className={interactive ? cn(
                  'group relative grid cursor-pointer grid-cols-3 px-3 py-1 font-mono text-xs transition-all',
                  swept ? 'bg-green-500/20' : 'hover:bg-green-500/10'
                ) : 'group relative grid grid-cols-3 px-3 py-1 font-mono text-xs transition-all'}
                onClick={interactive ? () => handleLevelClick(level, 'bid', index + 1) : undefined}
                onMouseEnter={interactive ? () => setHoveredLevel({ price: level.priceCents, side: 'bid' }) : undefined}
                onMouseLeave={interactive ? () => setHoveredLevel(null) : undefined}
              >
                {/* Depth bar */}
                <div
                  className={cn(
                    'pointer-events-none absolute inset-y-0 left-0 transition-colors',
                    swept ? 'bg-green-500/25' : 'bg-green-500/10'
                  )}
                  style={{ width: `${Math.min(depthPct, 100)}%` }}
                />
                <span className="relative z-10 font-semibold text-green-400">
                  {formatLevelCents(level.priceCents)}
                </span>
                <span className="text-muted-foreground relative z-10 text-right">
                  <span
                    key={formatContracts(level.size)}
                    className="animate-flash-neutral inline-block"
                  >
                    {formatContracts(level.size)}
                  </span>
                </span>
                <span className="text-muted-foreground relative z-10 text-right">
                  <span
                    key={formatCompactDollars(level.cumulativeDollars)}
                    className="animate-flash-neutral inline-block"
                  >
                    {formatCompactDollars(level.cumulativeDollars)}
                  </span>
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Sweep info footer (shows when hovering a level of a book that can seed the ticket; a hover left over from
          before the handler went away must not quote a sweep on a reference-only book) */}
      {interactive && hoveredLevel && (
        <div className="border-border bg-muted/50 border-t px-3 py-1.5">
          {(() => {
            const levels = hoveredLevel.side === 'ask' ? displayAsks : displayBids;
            const hovered = levels.find((l) => l.priceCents === hoveredLevel.price);
            if (!hovered) return null;
            const payout = hovered.cumulativeContracts; // $1 per contract if YES
            const cost = hovered.cumulativeDollars;
            const profit = payout - cost;
            return (
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-muted-foreground">
                  Sweep {hovered.cumulativeContracts.toLocaleString()}{' '}
                  {contractsLabel.toLowerCase()}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">
                    Cost:{' '}
                    <Money
                      value={cost}
                      className="text-[10px]"
                    />
                  </span>
                  {hoveredLevel.side === 'ask' && profit > 0 && (
                    <span className="text-green-400">
                      Payout:{' '}
                      <Money
                        value={payout}
                        className="text-[10px]"
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
